/**
 * Eigenbau: Team — Personen und Gruppen.
 *
 * Die APIs `personen.setzeAktiv`, `personen.setzeGruppen`, `gruppen.*` lagen
 * fertig. Die Oberfläche zeigte bisher nur eine flache Personenliste.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RECHTE_REGISTRY } from "@cubic/shared";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Leerzustand } from "@/components/Leerzustand";
import { Textarea } from "@/components/Textarea";
import { Schalter } from "@/components/entwurf/Schalter";
import { initialen } from "@/lib/initialen";
import { api } from "@/lib/api";
import { useIch } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";
import {
  EinstellungenHuelle,
  EinstellungsAbschnitt,
  Erfolgsmeldung,
  FehlerBlock,
  LadenZeile,
  meldungFuer,
} from "./gemeinsam";

function hatRecht(
  rechte: readonly string[] | undefined,
  schluessel: string,
): boolean {
  return (
    (rechte ?? []).includes(schluessel) ||
    (rechte ?? []).includes("system.admin")
  );
}

export function EinstellungenTeam() {
  const client = useQueryClient();
  const ich = useIch();
  const kannBenutzer = hatRecht(ich.data?.rechte, "benutzer.verwalten");
  const kannGruppen = hatRecht(ich.data?.rechte, "gruppen.verwalten");

  const [ok, setzeOk] = useState<string | null>(null);
  const [fehler, setzeFehler] = useState<Meldung | null>(null);
  const [gruppenName, setzeGruppenName] = useState("");
  const [gruppenBeschreibung, setzeGruppenBeschreibung] = useState("");
  const [gruppenRechte, setzeGruppenRechte] = useState<string[]>([]);
  const [bearbeitePerson, setzeBearbeitePerson] = useState<string | null>(null);

  const personen = useQuery({
    queryKey: ["cubic", "personen"],
    queryFn: () => api.personen.liste.query(),
  });

  const gruppen = useQuery({
    queryKey: ["cubic", "gruppen"],
    queryFn: () => api.gruppen.liste.query(),
    enabled: kannGruppen || kannBenutzer,
  });

  const personGruppen = useQuery({
    queryKey: ["cubic", "personen", "gruppen", bearbeitePerson],
    queryFn: () => api.personen.gruppen.query({ id: bearbeitePerson! }),
    enabled: Boolean(bearbeitePerson) && kannBenutzer,
  });

  const setzeAktiv = useMutation({
    mutationFn: (e: { id: string; aktiv: boolean }) =>
      api.personen.setzeAktiv.mutate(e),
    onSuccess: async () => {
      setzeFehler(null);
      setzeOk("Aktivstatus gespeichert.");
      await client.invalidateQueries({ queryKey: ["cubic", "personen"] });
    },
    onError: (e) => {
      setzeOk(null);
      setzeFehler(meldungFuer(e, "Aktivstatus konnte nicht geändert werden."));
    },
  });

  const setzeGruppen = useMutation({
    mutationFn: (e: { personId: string; gruppeIds: string[] }) =>
      api.personen.setzeGruppen.mutate(e),
    onSuccess: async () => {
      setzeFehler(null);
      setzeOk("Gruppenzuordnung gespeichert.");
      await client.invalidateQueries({
        queryKey: ["cubic", "personen", "gruppen"],
      });
    },
    onError: (e) => {
      setzeOk(null);
      setzeFehler(
        meldungFuer(e, "Gruppenzuordnung konnte nicht gespeichert werden."),
      );
    },
  });

  const legeGruppeAn = useMutation({
    mutationFn: () =>
      api.gruppen.anlegen.mutate({
        name: gruppenName.trim(),
        beschreibung: gruppenBeschreibung.trim() || undefined,
        rechte: gruppenRechte,
      }),
    onSuccess: async () => {
      setzeGruppenName("");
      setzeGruppenBeschreibung("");
      setzeGruppenRechte([]);
      setzeFehler(null);
      setzeOk("Gruppe angelegt.");
      await client.invalidateQueries({ queryKey: ["cubic", "gruppen"] });
    },
    onError: (e) => {
      setzeOk(null);
      setzeFehler(meldungFuer(e, "Gruppe konnte nicht angelegt werden."));
    },
  });

  const loescheGruppe = useMutation({
    mutationFn: (id: string) => api.gruppen.loeschen.mutate({ id }),
    onSuccess: async () => {
      setzeFehler(null);
      setzeOk("Gruppe gelöscht.");
      await client.invalidateQueries({ queryKey: ["cubic", "gruppen"] });
    },
    onError: (e) => {
      setzeOk(null);
      setzeFehler(meldungFuer(e, "Gruppe konnte nicht gelöscht werden."));
    },
  });

  const sortiertePersonen = useMemo(() => {
    return [...(personen.data ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name, "de") || a.id.localeCompare(b.id),
    );
  }, [personen.data]);

  const eigeneRechte = new Set(ich.data?.rechte ?? []);
  const vergebareRechte = RECHTE_REGISTRY.filter(
    (r) =>
      eigeneRechte.has("system.admin") || eigeneRechte.has(r.schluessel),
  );

  const gewaehlteGruppen = new Set(personGruppen.data ?? []);

  return (
    <EinstellungenHuelle
      aktiv="team"
      beschreibung="Personen dieser Instanz und die Gruppen mit ihren Rechten."
    >
      {fehler ? <Fehlerzeile meldung={fehler} /> : null}
      <Erfolgsmeldung text={ok} />

      <EinstellungsAbschnitt
        titel="Personen"
        beschreibung={
          kannBenutzer
            ? "Aktivstatus und Gruppenzuordnung. Konten entstehen über Authentik."
            : "Sichtbar sind nur Personen, die deine Rolle freigibt — ohne Verwaltungsrecht oft nur du selbst."
        }
      >
        {personen.isLoading || ich.isLoading ? (
          <LadenZeile />
        ) : personen.isError ? (
          <FehlerBlock
            fehler={personen.error}
            fallback="Personen konnten nicht geladen werden."
            onErneut={() => void personen.refetch()}
          />
        ) : sortiertePersonen.length === 0 ? (
          <Leerzustand
            titel="Keine Personen."
            hinweis="Personen entstehen bei der ersten Anmeldung über Authentik."
          />
        ) : (
          <ul className="divide-y divide-linie">
            {sortiertePersonen.map((p) => (
              <li key={p.id} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className="flex size-9 shrink-0 items-center justify-center rounded-full bg-wisch font-mono text-[11px] font-medium text-tinte2"
                      aria-hidden="true"
                    >
                      {initialen(p.name)}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[14.5px] text-tinte">
                        {p.name}
                        {p.id === ich.data?.personId ? (
                          <span className="ml-2 font-mono text-[11px] text-tinte3">
                            du
                          </span>
                        ) : null}
                      </p>
                      <p className="truncate font-mono text-[11.5px] text-tinte3">
                        {p.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Badge variant={p.aktiv === 1 ? "success" : "neutral"}>
                      {p.aktiv === 1 ? "aktiv" : "inaktiv"}
                    </Badge>
                    {kannBenutzer && p.id !== ich.data?.personId ? (
                      <Schalter
                        an={p.aktiv === 1}
                        ariaLabel={`${p.name} aktiv`}
                        onKlick={() =>
                          setzeAktiv.mutate({
                            id: p.id,
                            aktiv: p.aktiv !== 1,
                          })
                        }
                      />
                    ) : null}
                    {kannBenutzer ? (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          setzeBearbeitePerson((aktuell) =>
                            aktuell === p.id ? null : p.id,
                          )
                        }
                      >
                        {bearbeitePerson === p.id
                          ? "Gruppen schließen"
                          : "Gruppen"}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {bearbeitePerson === p.id ? (
                  <div className="mt-3 space-y-3 bg-wisch p-4">
                    <p className="font-mono text-[11.5px] text-tinte3">
                      Gruppen von {p.name}
                    </p>
                    {personGruppen.isLoading ? (
                      <LadenZeile />
                    ) : personGruppen.isError ? (
                      <FehlerBlock
                        fehler={personGruppen.error}
                        fallback="Gruppen konnten nicht geladen werden."
                        onErneut={() => void personGruppen.refetch()}
                      />
                    ) : (
                      <ul className="space-y-2">
                        {(gruppen.data ?? []).map((g) => {
                          const an = gewaehlteGruppen.has(g.id);
                          return (
                            <li
                              key={g.id}
                              className="flex items-center justify-between gap-3"
                            >
                              <span className="text-sm text-tinte">
                                {g.name}
                              </span>
                              <Schalter
                                an={an}
                                ariaLabel={`Gruppe ${g.name}`}
                                onKlick={() => {
                                  const naechste = new Set(gewaehlteGruppen);
                                  if (an) naechste.delete(g.id);
                                  else naechste.add(g.id);
                                  setzeGruppen.mutate({
                                    personId: p.id,
                                    gruppeIds: [...naechste],
                                  });
                                }}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </EinstellungsAbschnitt>

      <EinstellungsAbschnitt
        titel="Gruppen"
        beschreibung={
          kannGruppen
            ? "Rechte hängen an Gruppen, nie direkt an Personen."
            : "Gruppenverwaltung braucht das Recht „Gruppen verwalten“."
        }
      >
        {!kannGruppen ? (
          <p className="text-sm text-tinte3">
            Ohne Verwaltungsrecht siehst du hier keine Gruppenbearbeitung.
          </p>
        ) : gruppen.isLoading ? (
          <LadenZeile />
        ) : gruppen.isError ? (
          <FehlerBlock
            fehler={gruppen.error}
            fallback="Gruppen konnten nicht geladen werden."
            onErneut={() => void gruppen.refetch()}
          />
        ) : (
          <>
            <ul className="divide-y divide-linie">
              {(gruppen.data ?? []).map((g) => (
                <li
                  key={g.id}
                  className="flex flex-wrap items-start justify-between gap-3 py-3"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[14.5px] text-tinte">{g.name}</p>
                      {g.istSystem === 1 ? (
                        <Badge variant="neutral">System</Badge>
                      ) : null}
                      <span className="font-mono text-[11px] text-tinte3">
                        {g.mitglieder}{" "}
                        {g.mitglieder === 1 ? "Mitglied" : "Mitglieder"}
                      </span>
                    </div>
                    {g.beschreibung ? (
                      <p className="text-[12.5px] text-tinte3">
                        {g.beschreibung}
                      </p>
                    ) : null}
                    <p className="font-mono text-[11px] leading-relaxed text-tinte4">
                      {(g.rechte ?? []).length === 0
                        ? "keine Rechte"
                        : (g.rechte as string[]).join(" · ")}
                    </p>
                  </div>
                  {g.istSystem !== 1 ? (
                    <Button
                      variant="secondary"
                      onClick={() => loescheGruppe.mutate(g.id)}
                      disabled={loescheGruppe.isPending}
                      isLoading={
                        loescheGruppe.isPending &&
                        loescheGruppe.variables === g.id
                      }
                    >
                      Löschen
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>

            <div className="mt-4 space-y-3 border-t border-linie pt-4">
              <p className="font-mono text-[12px] font-medium text-tinte">
                Gruppe anlegen
              </p>
              <div>
                <Label htmlFor="gruppe-name">Name</Label>
                <Input
                  id="gruppe-name"
                  className="mt-1.5"
                  value={gruppenName}
                  maxLength={100}
                  onChange={(e) => setzeGruppenName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="gruppe-beschreibung">Beschreibung</Label>
                <Textarea
                  id="gruppe-beschreibung"
                  className="mt-1.5"
                  rows={2}
                  value={gruppenBeschreibung}
                  maxLength={2000}
                  onChange={(e) => setzeGruppenBeschreibung(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <p className="text-[12.5px] text-tinte3">
                  Rechte (nur was du selbst hast)
                </p>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {vergebareRechte.map((r) => {
                    const an = gruppenRechte.includes(r.schluessel);
                    return (
                      <li
                        key={r.schluessel}
                        className="flex items-start justify-between gap-2"
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-tinte">{r.label}</p>
                          <p className="font-mono text-[10.5px] text-tinte4">
                            {r.schluessel}
                          </p>
                        </div>
                        <Schalter
                          an={an}
                          ariaLabel={r.label}
                          onKlick={() =>
                            setzeGruppenRechte((liste) =>
                              an
                                ? liste.filter((x) => x !== r.schluessel)
                                : [...liste, r.schluessel],
                            )
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
              <Button
                onClick={() => legeGruppeAn.mutate()}
                disabled={
                  legeGruppeAn.isPending || gruppenName.trim().length === 0
                }
                isLoading={legeGruppeAn.isPending}
              >
                Gruppe anlegen
              </Button>
            </div>
          </>
        )}
      </EinstellungsAbschnitt>
    </EinstellungenHuelle>
  );
}
